/**
 * ImagePassageDrawer
 *
 * In-app viewer for cited IMAGE sources (PNG/JPG). An image has no text layer
 * to quote-anchor in, so the cited passage is shown via OCR bbox provenance:
 * normalized [0..1] regions from index time are drawn as percent-positioned
 * overlays on the displayed image — independent of display size, following
 * the Textract ratio-coordinate convention. Without regions the drawer still
 * shows the image in-app (better than a raw browser tab), just unhighlighted.
 *
 * A chart is usually cited through several passages: the title is one chunk,
 * the data values another. Showing only the clicked one left the numbers
 * unmarked even though their boxes exist, so every cited passage of the
 * document is drawn — the clicked one solid, the rest lighter — and a switch
 * in the header steps through them.
 */

import React from 'react';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ImageOutlined as ImageOutlinedIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LocatorRegion } from '../../api/chatTypes';
import {
  buildOriginalDocumentUrl,
  viewOriginalDocument,
} from '../../utils/documents/documentHelpers';

/** One cited window of the image: its boxes plus how it is labelled. */
export interface ImagePassage {
  /** Normalized [0..1] bounding boxes of the cited text on the image. */
  regions?: LocatorRegion[] | null;
  /** Citation label shown on the passage frame, e.g. "1.2". */
  label?: string | null;
  /**
   * False for the context passages drawn alongside the one the user clicked:
   * framed lighter, never the initial selection.
   */
  primary?: boolean | null;
}

export interface ImagePassageTarget extends ImagePassage {
  documentId: string;
  title?: string;
  /**
   * Every cited passage of the document. When set, all of them are framed
   * with their own labels and the header steps through them; otherwise the
   * inherited single-passage fields are used.
   */
  passages?: ImagePassage[] | null;
}

/** The passages to draw for a target: the explicit list, else the target itself. */
export function normalizeImagePassages(target: ImagePassageTarget): ImagePassage[] {
  if (target.passages && target.passages.length > 0) {
    return target.passages;
  }
  return [{ regions: target.regions, label: target.label, primary: true }];
}

const isDrawableRegion = (r: LocatorRegion): boolean =>
  r.w > 0 && r.h > 0 && r.x >= 0 && r.y >= 0 && r.x <= 1 && r.y <= 1;

interface ImagePassageDrawerProps {
  target: ImagePassageTarget | null;
  onClose: () => void;
}

export const ImagePassageDrawer: React.FC<ImagePassageDrawerProps> = ({ target, onClose }) => {
  const { t } = useTranslation();
  const open = target !== null;

  // Only passages that actually carry drawable boxes can be stepped through;
  // a caption-only chunk has no location on the image.
  const passages = React.useMemo(() => {
    if (!target) { return []; }
    return normalizeImagePassages(target)
      .map((p) => ({ ...p, regions: (p.regions ?? []).filter(isDrawableRegion) }))
      .filter((p) => p.regions.length > 0);
  }, [target]);

  // Start on the passage the user clicked; fall back to the first one.
  const initialIndex = React.useMemo(() => {
    const idx = passages.findIndex((p) => p.primary !== false);
    return idx >= 0 ? idx : 0;
  }, [passages]);
  const [activeIndex, setActiveIndex] = React.useState(0);
  React.useEffect(() => { setActiveIndex(initialIndex); }, [initialIndex, target]);

  const total = passages.length;
  const safeIndex = total > 0 ? Math.min(activeIndex, total - 1) : 0;
  const step = (delta: number) => {
    if (total > 1) { setActiveIndex((i) => (i + delta + total) % total); }
  };

  return (
    <Drawer
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100vw', md: 'min(920px, 92vw)' } } } }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #dde3ea',
        }}
      >
        <ImageOutlinedIcon sx={{ color: '#5e6b7a' }} />
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.95rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: '1 1 auto',
          }}
        >
          {target?.title ?? ''}
        </Typography>
        {total > 1 && (
          <>
            <Tooltip title={t('chat.sources.pdfViewer.previousPassage', { defaultValue: 'Vorherige Passage' })}>
              <IconButton size="small" onClick={() => step(-1)} aria-label="previous passage">
                <NavigateBeforeIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ color: '#5e6b7a', whiteSpace: 'nowrap' }}>
              {safeIndex + 1} / {total}
            </Typography>
            <Tooltip title={t('chat.sources.pdfViewer.nextPassage', { defaultValue: 'Nächste Passage' })}>
              <IconButton size="small" onClick={() => step(1)} aria-label="next passage">
                <NavigateNextIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>
          </>
        )}
        {total > 0 && (
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={t('chat.sources.pdfViewer.passageFound', { defaultValue: 'Passage markiert' })}
          />
        )}
        <Tooltip title={t('chat.sources.openDocument', { defaultValue: 'Open document' })}>
          <IconButton
            size="small"
            onClick={() => {
              if (target) {
                void viewOriginalDocument(target.documentId);
              }
            }}
            aria-label="open in new tab"
          >
            <OpenInNewIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: '1 1 auto', overflow: 'auto', backgroundColor: '#e8ebef', p: 2 }}>
        {target && (
          <Box
            sx={{
              position: 'relative',
              display: 'block',
              margin: '0 auto',
              maxWidth: '100%',
              width: 'fit-content',
              boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
              backgroundColor: '#ffffff',
            }}
          >
            <Box
              component="img"
              src={buildOriginalDocumentUrl(target.documentId)}
              alt={target.title ?? ''}
              sx={{ display: 'block', maxWidth: '100%' }}
            />
            {passages.flatMap((passage, pIdx) => {
              // The selected passage is drawn solid; the others stay visible
              // but recede, so the whole citation is legible at a glance.
              const active = pIdx === safeIndex;
              return passage.regions.map((r) => (
                <Box
                  key={`${pIdx}-${r.x}-${r.y}-${r.w}-${r.h}`}
                  sx={{
                    position: 'absolute',
                    left: `${r.x * 100}%`,
                    top: `${r.y * 100}%`,
                    width: `${r.w * 100}%`,
                    height: `${r.h * 100}%`,
                    backgroundColor: active
                      ? 'rgba(255, 213, 79, 0.35)'
                      : 'rgba(255, 213, 79, 0.12)',
                    outline: active
                      ? '2px solid rgba(255, 179, 0, 0.9)'
                      : '1px solid rgba(255, 179, 0, 0.45)',
                    borderRadius: '3px',
                    pointerEvents: 'none',
                    transition: 'background-color 120ms, outline-color 120ms',
                  }}
                />
              ));
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
